import React, { ReactNode, useMemo } from 'react';
import { Box, Button, Typography } from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';

interface CompactPaginationProps {
  currentPage: number;
  disabled?: boolean;
  onChange: (page: number) => void;
  totalPages: number;
}

const DOTS = 'dots';

const getPaginationItems = (
  currentPage: number,
  totalPages: number,
  siblingCount = 2,
): Array<number | typeof DOTS> => {
  const safeCurrentPage = Math.min(Math.max(1, currentPage), totalPages);
  const totalVisibleItems = siblingCount * 2 + 5;

  if (totalPages <= totalVisibleItems) {
    return Array.from({ length: totalPages }, (_, index) => index + 1);
  }

  const leftSibling = Math.max(safeCurrentPage - siblingCount, 2);
  const rightSibling = Math.min(safeCurrentPage + siblingCount, totalPages - 1);
  const items: Array<number | typeof DOTS> = [1];

  if (leftSibling > 2) {
    items.push(DOTS);
  }

  for (let page = leftSibling; page <= rightSibling; page += 1) {
    items.push(page);
  }

  if (rightSibling < totalPages - 1) {
    items.push(DOTS);
  }

  items.push(totalPages);

  return items;
};

const CompactPagination: React.FC<CompactPaginationProps> = ({
  currentPage,
  disabled = false,
  onChange,
  totalPages,
}) => {
  const paginationItems = useMemo(
    () => getPaginationItems(currentPage, totalPages),
    [currentPage, totalPages],
  );
  const canGoPrevious = currentPage > 1;
  const canGoNext = currentPage < totalPages;

  const renderItem = (item: number | typeof DOTS, index: number): ReactNode => {
    if (item === DOTS) {
      return (
        <Typography
          key={`${item}-${index}`}
          component={'span'}
          sx={{
            alignItems: 'center',
            display: 'inline-flex',
            height: 42,
            justifyContent: 'center',
            minWidth: 42,
          }}
        >
          {'...'}
        </Typography>
      );
    }

    const isActive = item === currentPage;

    return (
      <Button
        key={item}
        disabled={disabled}
        onClick={() => onChange(item)}
        variant={'outlined'}
        sx={{
          // Active page: solid primary fill + white text. Inactive: bordered
          // transparent. (Kept explicit here rather than via variant because
          // the theme renders `contained` as a bordered transparent button.)
          backgroundColor: isActive ? 'primary.main' : 'transparent',
          borderColor: isActive ? 'primary.main' : 'divider',
          borderWidth: 1,
          color: isActive ? 'common.white' : 'text.primary',
          fontSize: 18,
          fontWeight: isActive ? 700 : 500,
          height: 42,
          minHeight: 42,
          minWidth: 42,
          p: 0,
          '&:hover': {
            backgroundColor: isActive ? 'primary.main' : 'transparent',
            borderColor: 'primary.main',
            color: isActive ? 'common.white' : 'text.primary',
          },
          '&.Mui-disabled': {
            backgroundColor: 'transparent',
          },
        }}
      >
        {item}
      </Button>
    );
  };

  const renderNavigationButton = (
    icon: ReactNode,
    page: number,
    isDisabled: boolean,
  ) => (
    <Button
      disabled={disabled || isDisabled}
      onClick={() => onChange(page)}
      variant={'outlined'}
      sx={{
        backgroundColor: 'transparent',
        borderColor: 'divider',
        color: 'text.primary',
        height: 42,
        minHeight: 42,
        minWidth: 42,
        p: 0,
        '&:hover': {
          backgroundColor: 'transparent',
          borderColor: 'primary.main',
          color: 'text.primary',
        },
        '& svg': {
          color: 'text.primary',
        },
        '&:hover svg': {
          color: 'text.primary',
        },
        '&.Mui-disabled': {
          backgroundColor: 'transparent',
        },
        '&.Mui-disabled svg': {
          color: 'text.secondary',
        },
      }}
    >
      {icon}
    </Button>
  );

  return (
    <>
      <Box
        sx={{
          alignItems: 'center',
          display: {
            xs: 'none',
            sm: 'flex',
          },
          flexWrap: 'nowrap',
          gap: 1,
          justifyContent: 'center',
          overflowX: 'auto',
          width: '100%',
        }}
      >
        {renderNavigationButton(
          <ChevronLeftIcon />,
          currentPage - 1,
          !canGoPrevious,
        )}
        {paginationItems.map(renderItem)}
        {renderNavigationButton(
          <ChevronRightIcon />,
          currentPage + 1,
          !canGoNext,
        )}
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          display: {
            xs: 'flex',
            sm: 'none',
          },
          gap: 1,
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        {renderNavigationButton(
          <ChevronLeftIcon />,
          currentPage - 1,
          !canGoPrevious,
        )}
        <Typography component={'span'} sx={{ fontWeight: 600 }}>
          {`Page ${currentPage} of ${totalPages}`}
        </Typography>
        {renderNavigationButton(
          <ChevronRightIcon />,
          currentPage + 1,
          !canGoNext,
        )}
      </Box>
    </>
  );
};

export default CompactPagination;
